import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * A semantic `<dl>` for titled key/value sections — the "Финансы за смену" /
 * "Курьер" blocks in the inbox side panel and the profile stat blocks.
 *
 * Two layouts share the same markup:
 *
 * - `stacked` (default) — the muted label sits *over* a bold value. Reads well
 *   for numbers and multi-word values (e.g. "Наличные на руках" / "380 000 сум").
 * - `inline` — label on the left, value on the right of one row
 *   (`justify-between`). Compact key/value like the courier info.
 *
 * Presentational only: every label / value / hint comes in via props, so the
 * component knows nothing about orders, couriers, or any domain.
 *
 * ```tsx
 * <DescriptionList title="Финансы за смену">
 *   <DescriptionList.Item label="Наличные на руках" value="380 000 сум" />
 *   <DescriptionList.Item label="Заработано" value="290 000 сум" />
 * </DescriptionList>
 *
 * <DescriptionList title="Курьер" layout="inline">
 *   <DescriptionList.Item label="Номер телефона" value="+998 94 653 33 30" />
 *   <DescriptionList.Item label="Рейтинг" value="4.8" />
 * </DescriptionList>
 * ```
 */

type DescriptionListLayout = NonNullable<
  VariantProps<typeof descriptionListVariants>['layout']
>;

const descriptionListVariants = cva('flex flex-col', {
  variants: {
    layout: {
      stacked: 'gap-4',
      inline: 'gap-3',
    },
  },
  defaultVariants: {
    layout: 'stacked',
  },
});

const DescriptionListLayoutContext =
  React.createContext<DescriptionListLayout>('stacked');

type DescriptionListProps = React.ComponentProps<'dl'> &
  VariantProps<typeof descriptionListVariants> & {
    /** Optional section heading rendered above the list. */
    title?: React.ReactNode;
  };

function DescriptionListRoot({
  className,
  layout,
  title,
  children,
  ...props
}: DescriptionListProps) {
  const resolvedLayout: DescriptionListLayout = layout ?? 'stacked';
  return (
    <DescriptionListLayoutContext.Provider value={resolvedLayout}>
      {title !== undefined && title !== null ? (
        <p className="mb-3 text-base font-semibold tracking-tight text-foreground">
          {title}
        </p>
      ) : null}
      <dl
        data-slot="description-list"
        data-layout={resolvedLayout}
        className={cn(descriptionListVariants({ layout: resolvedLayout }), className)}
        {...props}
      >
        {children}
      </dl>
    </DescriptionListLayoutContext.Provider>
  );
}

type DescriptionListItemProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  /** The muted label (the `<dt>`). */
  label: React.ReactNode;
  /** The bold value (the `<dd>`). Ignored when `children` are provided. */
  value?: React.ReactNode;
  /** A smaller muted line rendered under the value. */
  hint?: React.ReactNode;
  /** Custom value content — takes precedence over `value`. */
  children?: React.ReactNode;
};

function DescriptionListItem({
  className,
  label,
  value,
  hint,
  children,
  ...props
}: DescriptionListItemProps) {
  const layout = React.useContext(DescriptionListLayoutContext);
  const isInline = layout === 'inline';
  return (
    <div
      data-slot="description-list-item"
      className={cn(
        isInline ? 'flex items-baseline justify-between gap-4' : 'flex flex-col',
        className,
      )}
      {...props}
    >
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'text-sm font-medium tracking-tight text-foreground',
          isInline ? 'text-right' : 'mt-0.5',
        )}
      >
        {children ?? value}
        {hint !== undefined && hint !== null ? (
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

const DescriptionList = Object.assign(DescriptionListRoot, {
  Item: DescriptionListItem,
});

export { DescriptionList, descriptionListVariants };
export type { DescriptionListProps, DescriptionListItemProps, DescriptionListLayout };
